import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { toast } from "@/hooks/use-toast";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { SliderCaptcha } from "@/components/auth/SliderCaptcha";
import { AuthLogo } from "@/components/auth/AuthLogo";
import { useHoneypot } from "@/hooks/useHoneypot";
import { useTimeProtection } from "@/hooks/useTimeProtection";
import { getStoredAffiliateCode } from "@/hooks/useAffiliateTracking";
import { sanitizeName } from "@/lib/sanitize";

const Register = () => {
  const { t, i18n } = useTranslation();
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const { isBot, honeypotProps } = useHoneypot();
  const { checkTiming } = useTimeProtection();
  
  // Check if user has already accepted cookies from banner
  const [hasCookieConsent, setHasCookieConsent] = useState(false);
  
  useEffect(() => {
    const cookieConsent = localStorage.getItem("cookie-consent");
    if (cookieConsent) {
      try {
        const parsed = JSON.parse(cookieConsent);
        // User has interacted with cookie banner if any consent is saved
        setHasCookieConsent(parsed.necessary === true);
      } catch {
        setHasCookieConsent(false);
      }
    }
  }, []);
  
  const returnTo = searchParams.get('returnTo');
  const subtitleKey = returnTo === '/become-professional' 
    ? "auth.verificationRegistrationRequired" 
    : "common.tagline";

  const formSchema = useMemo(() => z
    .object({
      firstName: z.string().min(1, { message: t("auth.firstNameRequired") }),
      lastName: z.string().min(1, { message: t("auth.lastNameRequired") }),
      email: z.string().email({ message: t("auth.emailInvalid") }),
      password: z.string().min(6, { message: t("auth.passwordMin") }),
      confirmPassword: z.string(),
      acceptPrivacy: z.boolean().refine((val) => val === true, {
        message: t("auth.privacyRequired"),
      }),
      acceptTerms: z.boolean().refine((val) => val === true, {
        message: t("auth.termsRequired"),
      }),
      acceptCookies: z.boolean(),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t("auth.passwordsDontMatch"),
      path: ["confirmPassword"],
    })
    .refine((data) => hasCookieConsent || data.acceptCookies === true, {
      message: t("auth.cookiesRequired"),
      path: ["acceptCookies"],
    }), [t, hasCookieConsent]);

  type FormData = z.infer<typeof formSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      acceptPrivacy: false,
      acceptTerms: false,
      acceptCookies: false,
    },
  });

  const acceptPrivacy = watch("acceptPrivacy");
  const acceptTerms = watch("acceptTerms");
  const acceptCookies = watch("acceptCookies");

  const onSubmit = async (data: FormData) => {
    // Anti-spam checks
    if (isBot) {
      // Blocco silenzioso per i bot
      console.warn("Bot detected via honeypot");
      return;
    }

    if (!captchaVerified) {
      toast({ title: t("auth.errors.captchaRequired"), variant: "error" });
      return;
    }

    const timingCheck = checkTiming();
    if (!timingCheck.isValid) {
      if (timingCheck.reason === "too_fast") {
        toast({ title: t("auth.errors.tooFast"), variant: "error" });
        return;
      }
    }

    setIsLoading(true);
    
    // If user accepts cookies during registration, save to localStorage
    if (!hasCookieConsent && data.acceptCookies) {
      const cookiePreferences = {
        necessary: true,
        analytics: true,
        marketing: true,
      };
      localStorage.setItem("cookie-consent", JSON.stringify(cookiePreferences));
    }
    
    // Get affiliate code from localStorage
    const affiliateCode = getStoredAffiliateCode();
    
    try {
      const { error } = await signUp(
        data.email,
        data.password,
        data.firstName,
        data.lastName,
        affiliateCode || undefined,
        (i18n.language || 'it').substring(0, 2)
      );

      if (error) {
        if (error.message.includes("already registered")) {
          toast({ title: t("auth.emailAlreadyExists"), variant: "error" });
        } else {
          toast({ title: error.message || t("auth.errorOccurred"), variant: "error" });
        }
      } else {
        toast({ title: t("auth.signUpSuccess"), variant: "success" });
        
        // Add user to MailerLite "iscritti" AND "newsletter" lists with sanitized names
        try {
          await supabase.functions.invoke("mailerlite-subscribe", {
            body: {
              email: data.email,
              name: sanitizeName(data.firstName),
              lastName: sanitizeName(data.lastName),
              listType: "iscritti",
              language: i18n.language || "it",
              affiliateCode: affiliateCode || undefined,
              addToNewsletter: true, // Aggiunge anche al gruppo newsletter
            },
          });
        } catch (mailerliteError) {
          console.error("MailerLite subscription error:", mailerliteError);
          // Non bloccare la registrazione se MailerLite fallisce
        }
        
        // Check for return URL from URL params
        const searchParams = new URLSearchParams(window.location.search);
        const returnTo = searchParams.get('returnTo');
        
        // Always redirect to onboarding first, passing the final destination
        if (returnTo && returnTo.startsWith('/')) {
          navigate(`/onboarding?returnTo=${encodeURIComponent(returnTo)}`);
        } else {
          navigate("/onboarding");
        }
      }
    } catch (error: any) {
      toast({ title: error.message || t("auth.errorOccurred"), variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center p-4">
      <BrutalCard className="w-full max-w-md">
        <div className="text-center mb-8">
          <AuthLogo />
          <h1 className="font-heading font-bold text-3xl text-dark mb-2">
            {t("auth.signUp")}
          </h1>
          <p className="text-muted-foreground font-body">
            {t(subtitleKey)}
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="firstName" className="font-heading font-bold uppercase text-xs mb-2 block">
                {t("auth.firstName")}
              </Label>
              <BrutalInput
                id="firstName"
                type="text"
                {...register("firstName")}
                disabled={isLoading}
              />
              {errors.firstName && (
                <p className="text-destructive text-sm mt-1">{errors.firstName.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="lastName" className="font-heading font-bold uppercase text-xs mb-2 block">
                {t("auth.lastName")}
              </Label>
              <BrutalInput
                id="lastName"
                type="text"
                {...register("lastName")}
                disabled={isLoading}
              />
              {errors.lastName && (
                <p className="text-destructive text-sm mt-1">{errors.lastName.message}</p>
              )}
            </div>
          </div>

          <div>
            <Label htmlFor="email" className="font-heading font-bold uppercase text-xs mb-2 block">
              {t("auth.email")}
            </Label>
            <BrutalInput
              id="email"
              type="email"
              {...register("email")}
              disabled={isLoading}
            />
            {errors.email && (
              <p className="text-destructive text-sm mt-1">{errors.email.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="password" className="font-heading font-bold uppercase text-xs mb-2 block">
              {t("auth.password")}
            </Label>
            <BrutalInput
              id="password"
              type="password"
              {...register("password")}
              disabled={isLoading}
            />
            {errors.password && (
              <p className="text-destructive text-sm mt-1">{errors.password.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="confirmPassword" className="font-heading font-bold uppercase text-xs mb-2 block">
              {t("auth.confirmPassword")}
            </Label>
            <BrutalInput
              id="confirmPassword"
              type="password"
              {...register("confirmPassword")}
              disabled={isLoading}
            />
            {errors.confirmPassword && (
              <p className="text-destructive text-sm mt-1">{errors.confirmPassword.message}</p>
            )}
          </div>

          {/* Honeypot field - nascosto per gli umani, visibile per i bot */}
          <input
            type="email"
            name="email_confirm"
            {...honeypotProps}
          />

          {/* Slider CAPTCHA */}
          <SliderCaptcha
            onVerify={setCaptchaVerified}
            onReset={() => setCaptchaVerified(false)}
          />

          {/* Legal Checkboxes */}
          <div className="space-y-3">
            {/* Privacy Policy Checkbox */}
            <div className="flex items-start gap-2">
              <Checkbox
                id="privacy"
                checked={acceptPrivacy}
                onCheckedChange={(checked) => setValue("acceptPrivacy", checked as boolean)}
                disabled={isLoading}
              />
              <Label
                htmlFor="privacy"
                className="text-sm font-body leading-tight cursor-pointer"
              >
                {t("auth.iHaveReadAndAccept")}{" "}
                <Link 
                  to="/privacy" 
                  target="_blank" 
                  className="text-primary font-bold hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  {t("auth.privacyPolicy")}
                </Link>
              </Label>
            </div>
            {errors.acceptPrivacy && (
              <p className="text-destructive text-sm">{errors.acceptPrivacy.message}</p>
            )}

            {/* Terms and Conditions Checkbox */}
            <div className="flex items-start gap-2">
              <Checkbox
                id="terms"
                checked={acceptTerms}
                onCheckedChange={(checked) => setValue("acceptTerms", checked as boolean)}
                disabled={isLoading}
              />
              <Label
                htmlFor="terms"
                className="text-sm font-body leading-tight cursor-pointer"
              >
                {t("auth.iHaveReadAndAccept")}{" "}
                <Link 
                  to="/terms" 
                  target="_blank" 
                  className="text-primary font-bold hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  {t("auth.termsAndConditions")}
                </Link>
              </Label>
            </div>
            {errors.acceptTerms && (
              <p className="text-destructive text-sm">{errors.acceptTerms.message}</p>
            )}

            {/* Cookie Policy Checkbox - Only visible if cookies not already accepted */}
            {!hasCookieConsent && (
              <>
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="cookies"
                    checked={acceptCookies}
                    onCheckedChange={(checked) => {
                      setValue("acceptCookies", checked as boolean);
                    }}
                    disabled={isLoading}
                  />
                  <Label
                    htmlFor="cookies"
                    className="text-sm font-body leading-tight cursor-pointer"
                  >
                    {t("auth.iHaveReadAndAccept")}{" "}
                    <Link 
                      to="/cookie-policy" 
                      target="_blank" 
                      className="text-primary font-bold hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t("auth.cookiePolicy")}
                    </Link>
                  </Label>
                </div>
                {errors.acceptCookies && (
                  <p className="text-destructive text-sm">{errors.acceptCookies.message}</p>
                )}
              </>
            )}
          </div>

          <BrutalButton
            type="submit"
            className="w-full"
            disabled={isLoading || !captchaVerified}
          >
            {isLoading ? t("auth.signingUp") : t("auth.createAccount")}
          </BrutalButton>

          <p className="text-center text-sm font-body text-muted-foreground">
            {t("auth.alreadyHaveAccount")}{" "}
            <Link to="/auth/login" className="text-primary font-bold hover:underline">
              {t("auth.signIn")}
            </Link>
          </p>
        </form>
      </BrutalCard>
    </div>
  );
};

export default Register;
